import { useRef, useState } from "react";
import { Table, Button } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import Select from "react-select";

import { useInitialDataImplementation } from "@/hooks/implementation/firstLoad/initialDataImplementation";
import { useEstateBulkLoader } from "@/hooks/implementation/firstLoad/bulkLoader";

import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";
import { toast } from "@/services/Utils";
import { customSelectorStyle } from "@/components/ReactSelect/NoOptions.styles";

import LoadInventoryToolbar from "./LoadInventoryToolbar";
import FooterButtons from "../Buttons/FooterButtons";

import "./rowsLoadStyle.scss";

const key = "initial_data_implementation";

const EstatesTableLoad = (props) => {
	const { addToast } = useToasts();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();

	const { mutate, isLoading } = useEstateBulkLoader();

	const { data } = useInitialDataImplementation();
	const [isLockedDownload, setIsLockedDownload] = useState(false);

	const fileInputRef = useRef(null);

	const optionsEstateParents = data.estateParents.map((parent) => ({
		value: parent.value,
		label: parent.label,
	}));

	const addRow = () => {
		const newEstate = [...data.estates, { hash: uuidv4(), display_name: "", estate_parent_id: "", isNew: true }];
		queryClient.setQueryData([key], { ...data, estates: newEstate });
	};

	const handleInputChange = (index, field, value) => {
		const estates = data.estates.map((row, i) => (i === index ? { ...row, [field]: value } : row));
		queryClient.setQueryData([key], { ...data, estates: estates });
	};

	const handleSelectChange = (index, newCampoValue) => {
		const estates = data.estates.map((row, i) => (i === index ? { ...row, estate_parent_id: newCampoValue } : row));
		queryClient.setQueryData([key], { ...data, estates: estates });
	};

	const removeRow = (index, isNew) => {
		if (!isNew) {
			Swal.fire({
				title: "Accion de borrado",
				html: "Esta accion tendra efecto al presionar <strong>GUARDAR</strong> o <strong>SIGUIENTE</strong>, ¿deseas proceder?",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#7fb02f",
				cancelButtonColor: "#d33",
				confirmButtonText: "Sí, eliminarlo!",
				cancelButtonText: "Cancelar",
				didOpen: () => {
					document.body.classList.remove("swal2-height-auto");
				},
			}).then((result) => {
				if (result.isConfirmed) {
					const newEstates = data.estates.filter((_, i) => i !== index);
					queryClient.setQueryData([key], { ...data, estates: newEstates });

					Swal.fire({
						title: "Eliminado",
						html: `La bodega ha sido eliminada`,
						icon: "success",
						confirmButtonColor: "#7fb02f",
						confirmButtonText: "OK",
						didOpen: () => {
							document.body.classList.remove("swal2-height-auto");
						},
					});
				} else {
					toast(addToast, "Accion cancelada por el usuario");
				}
			});
		} else {
			const newEstates = data.estates.filter((_, i) => i !== index);
			queryClient.setQueryData([key], { ...data, estates: newEstates });
			toast(addToast, `Bodega eliminado`);
		}
	};

	const handleExportToExcel = () => {
		setIsLockedDownload(true);
		const filename = "PlanillaCargaBodegas.xlsx";
		const params = {};

		const payload = {
			file: {
				key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
				params: params,
				show: true,
				url: "/client/implementation/excel_estates",
				name: filename,
				type: "excel",
				inExecution: false,
				inProgress: false,
				inChecked: false,
			},
		};

		new Promise((resolve, reject) => {
			try {
				dispatch({
					type: FETCHING_ADD_DOWNLOAD,
					payload: payload,
				});

				setTimeout(() => {
					resolve();
				}, 2000);
			} catch (error) {
				reject(error);
			}
		})
			.then(() => {
				setIsLockedDownload(false);
			})
			.catch(() => {
				setIsLockedDownload(false);
			});
	};

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const reader = new FileReader();

		reader.onload = (e) => {
			const binaryStr = e.target.result;
			const workbook = XLSX.read(binaryStr, { type: "binary" });
			const firstSheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[firstSheetName];
			const jsonData = XLSX.utils.sheet_to_json(worksheet);

			const updatedEstates = [...data.estates];
			const estateParentsMap = new Map();

			data.estateParents.forEach((estateParent) => {
				estateParentsMap.set(estateParent.name.toUpperCase(), estateParent.id);
			});

			const newRows = jsonData.map((item) => {
				const hash = item["CODIGO"] || uuidv4();
				const name = item["CAMPO"] || "";
				const estateParentId = estateParentsMap.get(name.toUpperCase()) || "";
				const isNew = !updatedEstates.some((estate) => estate.hash === hash);

				return {
					hash,
					estate_parent_id: estateParentId,
					display_name: item["BODEGA"],
					isNew,
				};
			});

			newRows.forEach((newRow) => {
				const existingIndex = updatedEstates.findIndex((estate) => estate.hash === newRow.hash);
				if (existingIndex !== -1) {
					updatedEstates[existingIndex] = newRow;
				} else {
					updatedEstates.push(newRow);
				}
			});

			queryClient.setQueryData([key], { ...data, estates: updatedEstates });
		};

		reader.readAsBinaryString(file);

		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const validEstates = () => {
		let isValidEstates = true;

		for (let i = 0; i < data?.estates.length; i++) {
			const estates = data.estates[i];

			if (!estates.display_name || estates.display_name === "") {
				isValidEstates = false;
				toast(addToast, `Se requiere que las bodegas tengan un nombre`);
				break;
			}

			if (!estates.estate_parent_id) {
				isValidEstates = false;
				toast(addToast, `Se requiere que las bodegas tengan un nombre y campo asignado`);
				break;
			}
		}

		return isValidEstates;
	};

	const saveEstate = ({ moveForward = false }) => {
		if (validEstates()) {
			const queryData = { ...queryClient.getQueryData([key]) };
			queryData.implementationStep = moveForward ? data.implementationStep + 1 : data.implementationStep;

			mutate(queryData, {
				onSuccess: (response) => {
					if (Object.prototype.hasOwnProperty.call(response, "estates")) {
						toast(addToast, response.message, "success");

						queryClient.setQueryData([key], {
							...data,
							estates: response.estates,
							implementationStep: response.implementationStep,
						});

						if (props.formWizardRef.current?.nextTab && moveForward) {
							props.formWizardRef.current.nextTab();
						}
					}
				},
			});
		}
	};

	if (data.estates.length === 0) {
		addRow();
	}

	return (
		<>
			<LoadInventoryToolbar
				handleExportToExcel={handleExportToExcel}
				handleFileUpload={handleFileUpload}
				isLockedDownload={isLockedDownload}
				fileInputRef={fileInputRef}
			/>
			<Table hover>
				<thead>
					<tr>
						<th className="border-0" style={{ width: "48%" }}>
							Bodega <small className="text-danger">*</small>
						</th>
						<th className="border-0" style={{ width: "48%" }}>
							Campo asociado <small className="text-danger">*</small>
						</th>
						<th className="border-0" style={{ width: "4%" }}></th>
					</tr>
				</thead>
				<tbody>
					{data.estates.map((row, index) => (
						<tr key={row.hash}>
							<td className="position-relative td-collapse">
								<input
									type="text"
									className="form-control"
									placeholder={`Ingrese el nombre de la bodega`}
									required
									value={row.display_name || ""}
									onChange={(e) => handleInputChange(index, "display_name", e.target.value)}
								/>
								{row.isNew && (
									<div className="diagonal-badge">
										<span className="badge badge-info">Nuevo</span>
									</div>
								)}
							</td>
							<td className="td-collapse">
								<Select
									styles={customSelectorStyle}
									value={optionsEstateParents.find((option) => option.value === row.estate_parent_id) || ""}
									placeholder={"Selecciona un campo"}
									options={optionsEstateParents}
									onChange={(selectedOption) => handleSelectChange(index, selectedOption.value)}
								/>
							</td>
							<td className="td-collapse">
								<Button variant="danger" onClick={() => removeRow(index, row.isNew)}>
									<i className="ti-trash"></i>
								</Button>
							</td>
						</tr>
					))}
					<tr>
						<td className="border-0">
							<button
								className="btn bg-agro-outline-color btn-block tr-btn"
								onClick={addRow}
								style={{ height: "30px", fontSize: "12px", width: "200px" }}
								disabled={isLoading}
							>
								<i className="ti-plus"></i> Agregar
							</button>
						</td>
					</tr>
				</tbody>
			</Table>

			<FooterButtons wizard={props.formWizardRef} currentIndex={props.index} handleSave={saveEstate} isLoading={isLoading} />
		</>
	);
};

export default EstatesTableLoad;
